/**
 * HTTP statuses whose answer is settled: retrying cannot change them.
 *
 * 401/403/404 are statements about authentication, authorization, or
 * existence, not about transport health. Kept in step with the `'access'` arm
 * of `classifyDownloadFailure`.
 */
const NON_RETRYABLE_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

/**
 * An `Error` that also carries the HTTP status of the response that produced it.
 *
 * `fetch` does not reject on a non-ok response, so a caller that wants to treat
 * one as a failure has to throw its own error and attach the status. Naming
 * that shape here — rather than restating `Error & { status?: number }` at each
 * throw site — keeps the produce side and the inspect side
 * ({@link isNonRetryableError}) describing the same contract.
 *
 * @property status - Status of the response, absent when the request never got
 * one.
 */
export interface StatusCarryingError extends Error {
  status?: number;
}

/**
 * True when a rejected request carries a status that retrying cannot fix.
 *
 * The parameter is `unknown` because that is what a `catch` clause yields under
 * `strict` (`useUnknownInCatchVariables`), and because a rejection genuinely
 * need not be a {@link StatusCarryingError} — `fetch` rejects with `TypeError`
 * on a transport fault and `DOMException` on abort, and callers of this module
 * reject with a plain result object. Narrowing rather than asserting is what
 * lets the status be read without a type cast.
 *
 * A failure with no readable status is treated as retryable: transport faults
 * (offline, DNS, CORS) carry none, and those are exactly the ones worth
 * retrying.
 *
 * @param rejection - The value a rejected request threw.
 * @returns Whether the failure is settled and should not be retried.
 *
 * @example
 * ```typescript
 * isNonRetryableError(Object.assign(new Error('x'), { status: 403 })); // → true
 * isNonRetryableError(new TypeError('Failed to fetch'));              // → false
 * ```
 */
export function isNonRetryableError(rejection: unknown): boolean {
  const _status = readRejectionStatus(rejection);

  return _status !== null && NON_RETRYABLE_STATUSES.has(_status);
}

/**
 * Reads the HTTP status off a rejection value, or `null` when it carries none.
 *
 * The single place that knows how to get a status out of an `unknown`, so no
 * caller needs a type assertion to do it. Narrowing via `typeof`/`in` keeps it
 * honest about the fact that a rejection may be anything at all.
 *
 * @param rejection - The value a rejected request threw.
 * @returns The numeric status, or `null` if there is not one to read.
 *
 * @example
 * ```typescript
 * readRejectionStatus(Object.assign(new Error('x'), { status: 403 })); // → 403
 * readRejectionStatus(new TypeError('Failed to fetch'));              // → null
 * readRejectionStatus('forbidden');                                    // → null
 * ```
 */
export function readRejectionStatus(rejection: unknown): number | null {
  if (
    typeof rejection === 'object' &&
    rejection !== null &&
    'status' in rejection &&
    typeof rejection.status === 'number'
  ) {
    return rejection.status;
  }

  return null;
}
